import { X } from 'lucide-react'

import { type ComparedVersion, labelFor, MAX_COMPARED, sameVersion, versionKey, versionLabel } from './versions'

import styles from './SummaryFeedbackIndexPage.module.scss'

import type {
  SummaryFeedbackStats,
  SummaryFeedbackStatsBucket,
  SummaryFeedbackVersion,
} from '@/api/meetingSummaryFeedback'
import RatingTrend from '@/components/RatingTrend'
import { DAY_MS } from '@/utils/dates'

export type TrendSeries = { key: string; label: string; color: string; stats: SummaryFeedbackStats | undefined }
export const SERIES_COLORS = ['#034f46', '#ab5223']

// The API caps day buckets at 366 days.
export const MAX_DAY_BUCKET_MS = 366 * DAY_MS

const CompareControl = ({
  compare,
  versions,
  colors,
  onCompare,
}: {
  compare: ComparedVersion[]
  versions: SummaryFeedbackVersion[]
  colors: string[]
  onCompare: (next: ComparedVersion[]) => void
}) => {
  const isFull = compare.length >= MAX_COMPARED
  const pickable = (row: SummaryFeedbackVersion): row is SummaryFeedbackVersion & ComparedVersion =>
    Boolean(row.promptVersion && row.model)
  const add = (key: string) => {
    const row = versions.find((candidate) => pickable(candidate) && versionKey(candidate) === key)
    if (!row || !pickable(row) || isFull) return
    onCompare([...compare, { promptVersion: row.promptVersion, model: row.model }])
  }
  return (
    <div className={styles.compare} role="group" aria-label="Compare versions">
      {compare.map((version, index) => {
        const label = labelFor(version, versions)
        return (
          <span className={styles.compareChip} key={versionKey(version)}>
            <span className={styles.swatch} style={{ background: colors[index] }} aria-hidden="true" />
            {label}
            <button
              type="button"
              className={styles.chipClear}
              aria-label={`Stop comparing ${label}`}
              onClick={() => onCompare(compare.filter((other) => !sameVersion(other, version)))}
            >
              <X size={13} />
            </button>
          </span>
        )
      })}
      <select
        className={styles.compareSelect}
        aria-label="Add a version to compare"
        value=""
        disabled={isFull}
        title={isFull ? `Compare up to ${MAX_COMPARED} versions` : undefined}
        onChange={(event) => add(event.target.value)}
      >
        <option value="">{isFull ? `Compare up to ${MAX_COMPARED} versions` : 'Compare a version…'}</option>
        {versions.map((row) => {
          const isPicked = pickable(row) && compare.some((version) => sameVersion(version, row))
          return (
            <option
              key={`${row.promptVersion}:${row.model}`}
              value={pickable(row) ? versionKey(row) : ''}
              disabled={!pickable(row) || isPicked}
            >
              {versionLabel(row)} ({row.total.toLocaleString()} {row.total === 1 ? 'rating' : 'ratings'})
            </option>
          )
        })}
      </select>
    </div>
  )
}

const RateTrend = ({
  since,
  until,
  bucket,
  isDayAllowed,
  onBucket,
  series,
  versions,
  compare,
  onCompare,
}: {
  since: string
  until: string
  bucket: SummaryFeedbackStatsBucket
  isDayAllowed: boolean
  onBucket: (bucket: SummaryFeedbackStatsBucket) => void
  series: TrendSeries[]
  versions: SummaryFeedbackVersion[]
  compare: ComparedVersion[]
  onCompare: (next: ComparedVersion[]) => void
}) => (
  <RatingTrend
    since={since}
    until={until}
    bucket={bucket}
    isDayAllowed={isDayAllowed}
    onBucket={onBucket}
    series={series}
    description={compare.length ? 'each line is one prompt + model' : 'current filters'}
    controls={
      <CompareControl
        compare={compare}
        versions={versions}
        colors={series.map((item) => item.color)}
        onCompare={onCompare}
      />
    }
  />
)

export default RateTrend
